import styled from '@emotion/styled'
import { gsColors } from '../../../styles/Variables'
import { mq } from '../../../helpers'

export const AboutUsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100vw;
  box-sizing: border-box;
  overflow-x: clip;
  margin: 0;
  padding: 0;

  ${mq('max', '768px')} {
    max-width: 100%;
  }
`

export const HeroEyebrow = styled.p`
  color: ${gsColors.salmon};
  font-family: Garnett;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.38;
  letter-spacing: 0.32px;
  text-transform: uppercase;
  text-align: center;
  margin: 0 0 24px 0;
`

// Overrides the shared <Hero> for the about-us page.
// Desktop: order:2 on cobalt panel = image left, panel right.
// Mobile: reset order so panel stays on top (matches mobile Figma 447:441).
// !important wins against shared <Hero>'s deeply-nested rules without class-doubling.
export const AboutHeroOverride = styled.div`
  width: 100%;

  .hero-container-inner {
    height: auto !important;
  }

  .content {
    order: 2 !important;
    height: auto !important;
    align-self: stretch !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    box-sizing: border-box !important;
    padding: 64px 48px !important;

    ${mq('max', '768px')} {
      order: 0 !important;
      align-self: auto !important;
      min-height: 0 !important;
      padding: 48px 16px !important;
    }
  }

  .content .inner {
    text-align: center !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;

    .hero-headline-group {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      width: 100%;

      ${mq('max', '768px')} {
        gap: 8px;
      }
    }

    .hero-headline {
      text-align: center !important;
      font-size: 82px !important;
      line-height: 1.1 !important;
      letter-spacing: 1.64px !important;
      width: 100% !important;

      ${mq('max', '1290px')} {
        font-size: 72px !important;
      }

      ${mq('max', '1024px')} {
        font-size: 64px !important;
        letter-spacing: 1.28px !important;
      }

      ${mq('max', '768px')} {
        font-size: 56px !important;
        line-height: 1.15 !important;
        letter-spacing: 1.12px !important;
      }

      ${mq('max', '380px')} {
        font-size: 48px !important;
        letter-spacing: 0.96px !important;
      }
    }

    .hero-subheadline {
      color: ${gsColors.salmon};
      font-family: Garnett;
      font-weight: 700;
      text-transform: uppercase;
      text-align: center;
      font-size: 42px;
      line-height: 1.38;
      letter-spacing: 0.84px;
      width: 100%;

      ${mq('max', '1290px')} {
        font-size: 38px;
      }

      ${mq('max', '1024px')} {
        font-size: 34px;
        letter-spacing: 0.68px;
      }

      ${mq('max', '768px')} {
        font-size: 28px;
        letter-spacing: 0.56px;
      }

      ${mq('max', '380px')} {
        font-size: 24px;
        letter-spacing: 0.48px;
      }
    }

    .about-hero-cta {
      align-self: center;
      width: max-content;
      flex-shrink: 0;
      white-space: nowrap;
      margin-top: 44px;
      letter-spacing: 1.44px;
      text-transform: capitalize;

      ${mq('max', '768px')} {
        width: 100%;
        margin-top: 32px;
        padding: 14px 0 !important;
        letter-spacing: 1.12px;
      }
    }
  }

  .image-placeholder {
    aspect-ratio: 1440 / 1496;
    height: auto !important;
    min-height: 0 !important;
  }
`

export const MissionStatsWrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  background-color: ${gsColors.white};
  padding: 88px 88px 96px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${mq('max', '768px')} {
    padding: 50px 16px;
  }

  .our-story-title {
    align-self: center;
    margin: 0 0 40px 0;

    ${mq('max', '768px')} {
      margin: 0 0 24px 0;
    }
  }

  .mission-copy {
    width: 100%;
    margin-bottom: 56px;
  }

  .stats-row {
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 40px;
    margin-bottom: 56px;

    ${mq('max', '1024px')} {
      flex-direction: column;
      gap: 24px;
    }
  }

  .stat {
    flex: 1;
    padding-left: 24px;
    border-left: 1px solid ${gsColors.cobalt};

    .stat-value {
      margin: 0 0 8px 0;
    }

    .stat-label {
      margin: 0;
    }
  }

  .cta-block {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 26px;
    padding: 36px 0 48px;
  }

  .cta-button {
    padding: 14px 80px;
    letter-spacing: 1.12px;
    text-transform: capitalize;
  }
`

export const PartnershipsWrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  background-color: ${gsColors.khaki};
  padding: 88px 88px 96px;
  display: flex;
  flex-direction: column;

  ${mq('max', '768px')} {
    padding: 50px 16px;
  }

  .section-heading {
    margin: 0 0 60px 0;

    ${mq('max', '768px')} {
      margin: 0 0 32px 0;
    }
  }

  .partnerships-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 80px;

    ${mq('max', '768px')} {
      grid-template-columns: 1fr;
      gap: 40px;
      margin-bottom: 48px;
    }
  }

  .partnership-item {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .partnership-copy {
    display: flex;
    flex-direction: column;
    max-width: 540px;

    ${mq('max', '768px')} {
      order: 2;
      max-width: none;
    }
  }

  .partnership-image {
    position: relative;
    width: 100%;
    aspect-ratio: 623 / 544;
    background-color: ${gsColors.lightTangerine};
    overflow: hidden;
    margin-top: auto;

    ${mq('max', '768px')} {
      order: 1;
      margin-top: 0;
    }
  }

  .partnership-name {
    font-family: Euclid;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.38;
    letter-spacing: 0.2px;
    color: ${gsColors.black};
    margin: 0;
  }

  .partnership-role {
    font-family: Euclid;
    font-size: 20px;
    font-weight: 400;
    font-style: italic;
    line-height: 1.38;
    letter-spacing: 0.2px;
    color: ${gsColors.black};
    margin: 0 0 16px 0;
  }

  .cta-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 16px;
    width: 100%;
    align-self: center;
  }

  .partnerships-email {
    color: ${gsColors.black};
    font-weight: 600;
    text-decoration: underline;
  }
`

export const TopTierTalentWrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  background-color: ${gsColors.khaki};
  padding: 88px 88px 96px;
  display: flex;
  flex-direction: column;

  ${mq('max', '768px')} {
    padding: 50px 16px;
  }

  .section-heading {
    margin: 0 0 60px 0;

    ${mq('max', '768px')} {
      margin: 0 0 32px 0;
    }
  }

  .talent-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 80px;

    ${mq('max', '768px')} {
      grid-template-columns: 1fr;
      gap: 40px;
      margin-bottom: 48px;
    }
  }

  .talent-item {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .talent-copy {
    display: flex;
    flex-direction: column;
    max-width: 540px;
  }

  .talent-name {
    font-family: Euclid;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.38;
    letter-spacing: 0.2px;
    color: ${gsColors.black};
    margin: 0;
  }

  .talent-role {
    font-family: Euclid;
    font-size: 20px;
    font-weight: 400;
    font-style: italic;
    line-height: 1.38;
    letter-spacing: 0.2px;
    color: ${gsColors.black};
    margin: 0 0 16px 0;
  }

  .talent-image {
    position: relative;
    width: 100%;
    aspect-ratio: 623 / 544;
    background-color: ${gsColors.lightTangerine};
    overflow: hidden;
    margin-top: auto;
  }

  .cta-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 26px;
    width: 100%;
    align-self: center;
  }
`

export const CardGridWrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  background-color: ${gsColors.white};
  padding: 88px 88px 96px;
  display: flex;
  flex-direction: column;

  ${mq('max', '768px')} {
    padding: 50px 16px;
  }

  .section-heading {
    margin: 0 0 60px 0;
  }

  .cards-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 80px;

    ${mq('max', '768px')} {
      grid-template-columns: 1fr;
      gap: 40px;
      margin-bottom: 48px;
    }
  }

  .card {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .card-copy {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .card-image {
    position: relative;
    width: 100%;
    aspect-ratio: 623 / 544;
    background-color: ${gsColors.lightTangerine};
  }

  .cta-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }

  .cta-block-text {
    text-align: center;
  }
`

export const TestimonialsWrapper = styled.section`
  box-sizing: border-box;
  width: 100%;
  max-width: 1600px;
  display: flex;
  flex-direction: column;
  padding: 96px 0 96px 88px;
  background-color: ${props => props.bgColor || gsColors.white};
  overflow: hidden;

  ${mq('max', '768px')} {
    padding: 60px 0 60px 16px;
  }

  .carousel-heading {
    margin: 0 0 48px 0;

    ${mq('max', '768px')} {
      margin: 0 0 32px 0;
    }
  }

  .carousel-container {
    width: 100%;

    .swiper {
      overflow: visible;
    }
  }

  .carousel-slide {
    width: 411px !important;
    height: 460px !important;
    flex-shrink: 0;

    ${mq('max', '768px')} {
      width: calc(100vw - 40px) !important;
      height: 420px !important;
    }
  }

  .review-card {
    width: 100%;
    height: 100%;
    background-color: ${gsColors.cobalt};
    padding: 58px 30px 30px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    ${mq('max', '768px')} {
      padding: 48px 32px;
    }
  }

  .review-stars {
    display: flex;
    flex-direction: row;
    gap: 4px;

    svg {
      width: 32px;
      height: 32px;
    }
  }

  .review-text {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 20px;
    margin-top: 43px;
    color: ${gsColors.white};

    ${mq('max', '768px')} {
      margin-top: 32px;
      gap: 16px;
    }
  }

  .review-title {
    font-family: Euclid;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.38;
    color: ${gsColors.white};
    margin: 0;

    ${mq('max', '768px')} {
      font-size: 20px;
    }
  }

  .review-quote {
    font-family: Euclid;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.45;
    color: ${gsColors.white};
    margin: 0;
  }

  .review-name {
    font-family: Euclid;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.45;
    letter-spacing: 0.16px;
    color: ${gsColors.white};
    margin: 0;
    margin-top: auto;
  }

  .photo-card {
    position: relative;
    width: 100%;
    height: 100%;
    background-color: ${gsColors.lightTangerine};
    overflow: hidden;
  }
`

export const PressWrapper = styled.section`
  box-sizing: border-box;
  width: 100%;
  max-width: 1600px;
  background-color: ${gsColors.white};
  padding: 0 88px 72px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 56px;

  ${mq('max', '768px')} {
    flex-direction: column;
    gap: 28px;
    padding: 0 16px 50px;
  }

  .press-label {
    flex-shrink: 0;
    font-family: Euclid;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.45;
    letter-spacing: 0.96px;
    color: ${gsColors.black};
    white-space: nowrap;
  }

  .press-logos {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 32px 56px;
    margin: 0;
    padding: 0;
    list-style: none;

    ${mq('max', '768px')} {
      gap: 28px 40px;
    }
  }

  .press-logo {
    display: flex;
    align-items: center;
  }

  .press-logo-link {
    display: inline-flex;
    align-items: center;
    transition: opacity 0.15s ease;
  }

  .press-logo-link:hover {
    opacity: 0.6;
  }

  .press-logo-link img {
    height: var(--logo-h, 28px);
    width: auto;
    display: block;

    ${mq('max', '768px')} {
      height: calc(var(--logo-h, 28px) * 0.82);
    }
  }
`

export const BookCTAWrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  background-color: ${gsColors.white};
  padding: 88px 88px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  ${mq('max', '768px')} {
    padding: 50px 16px 0;
    gap: 12px;
  }

  .book-now-btn {
    margin-top: 16px;
    letter-spacing: 1.44px;

    ${mq('max', '768px')} {
      padding: 14px 48px;
    }
  }
`

export const MarketsMapWrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  background-color: ${gsColors.white};
  padding: 96px 88px 72px;
  display: flex;
  flex-direction: column;

  ${mq('max', '768px')} {
    padding: 60px 16px 40px;
  }

  .map-heading {
    margin: 0 0 48px 0;
    align-self: center;
  }

  .map-image {
    width: 100%;
    max-width: 885px;
    height: auto;
    margin: 0 auto 56px;
    align-self: center;

    ${mq('max', '768px')} {
      margin-bottom: 32px;
    }
  }
`

export const FAQsWrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  background-color: ${gsColors.white};
  padding: 72px 88px 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;

  ${mq('max', '768px')} {
    padding: 40px 16px 60px;
    gap: 12px;
  }

  .faq-link {
    color: ${gsColors.black};
    text-decoration: underline;
    text-transform: capitalize;
  }
`

export const FoundersQuoteWrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  background-color: ${gsColors.white};
  padding: 96px 88px 120px;
  display: flex;
  flex-direction: column;
  gap: 56px;

  ${mq('max', '768px')} {
    padding: 32px 16px;
    gap: 27px;
  }

  .quote {
    margin: 0;
    padding-left: 32px;
    border-left: 1px solid ${gsColors.black};
    max-width: 1040px;
    align-self: center;
    width: 100%;

    ${mq('max', '768px')} {
      padding-left: 19px;
      border-left-width: 2px;
    }
  }

  .quote-text {
    font-size: 32px;
    letter-spacing: 0.32px;

    ${mq('max', '768px')} {
      font-size: 18px;
      letter-spacing: 0.18px;
    }
  }

  .signatures {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 80px;
    align-self: center;
    max-width: 1040px;
    width: 100%;
    padding-left: 32px;
    box-sizing: border-box;

    ${mq('max', '768px')} {
      flex-direction: column;
      align-items: flex-start;
      gap: 27px;
      padding-left: 0;
    }
  }

  .signature {
    position: relative;
    flex: 1 1 0;
    height: 65px;
    min-width: 0;
  }

  ${mq('max', '768px')} {
    .signature {
      flex: 0 0 auto;
    }

    .signature-gio {
      width: 261px;
      height: 21px;
    }

    .signature-dave {
      width: 222px;
      height: 33px;
    }
  }
`

export const FoundersWrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  background-color: ${gsColors.khaki};
  padding: 64px 88px 0 88px;
  display: flex;
  flex-direction: column;

  ${mq('max', '1024px')} {
    padding: 40px 16px 40px 16px;
  }

  .founders-heading {
    margin: 0 0 40px 0;

    ${mq('max', '768px')} {
      margin: 0 0 24px 0;
    }
  }

  .founders-image-mobile {
    display: none;

    ${mq('max', '1024px')} {
      display: block;
      width: calc(100% + 16px);
      height: auto;
      margin: 0 -16px 32px 0;
    }
  }

  .founders-image-desktop {
    position: relative;
    width: 340px;
    aspect-ratio: 648 / 1294;
    flex-shrink: 0;

    ${mq('max', '1290px')} {
      width: 260px;
    }

    ${mq('max', '1024px')} {
      display: none;
    }
  }

  .founders-grid {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: space-between;
    gap: 60px;

    ${mq('max', '1024px')} {
      flex-direction: column;
      gap: 0;
      align-items: stretch;
    }
  }

  .founders-column {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 80px;
    padding-left: 107px;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 820px;

    ${mq('max', '1290px')} {
      padding-left: 32px;
      max-width: none;
    }

    ${mq('max', '1024px')} {
      padding-left: 0;
      gap: 32px;
      max-width: none;
    }
  }

  .founder-bio {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .founder-name {
    letter-spacing: 0.32px;
  }

  .founder-name-link,
  .bio-link {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    transition: opacity 0.15s ease;
  }

  .founder-name-link:hover,
  .bio-link:hover {
    opacity: 0.7;
  }
`
